// ============================================
// API SERVICE - COMPLETE (NO DUPLICATES)
// ============================================

import axios from 'axios';

// API Base URL from environment variable
const API_URL = import.meta.env.VITE_API_URL || 'https://sales.moahisolutions.com/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// AUTH SERVICE
// ============================================

const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: async () => {
    await api.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

// ============================================
// DASHBOARD SERVICE
// ============================================

const dashboardService = {
  getSummary: async () => {
    const response = await api.get('/dashboard/summary');
    return response;
  },

  getPipelineStatus: async () => {
    const response = await api.get('/dashboard/pipeline-status');
    return response;
  },

  getMonthlyTrend: async () => {
    const response = await api.get('/dashboard/monthly-trend');
    return response;
  },

  getGeography: async () => {
    const response = await api.get('/dashboard/geography');
    return response;
  },

  getSectorPerformance: async () => {
    const response = await api.get('/dashboard/sector-performance');
    return response;
  },

  getConsultantPerformance: async () => {
    const response = await api.get('/dashboard/consultant-performance');
    return response;
  },

  getLeadSourcePerformance: async () => {
    const response = await api.get('/dashboard/lead-source-performance');
    return response;
  },

  getUpcomingFollowUps: async () => {
    const response = await api.get('/dashboard/upcoming-followups');
    return response;
  },

  getRecentActivity: async () => {
    const response = await api.get('/dashboard/recent-activity');
    return response;
  },
};

// ============================================
// LEAD SERVICE
// ============================================

const leadService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/leads', { params: filters });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/leads/${id}`);
    return response;
  },

  create: async (leadData) => {
    const response = await api.post('/leads', leadData);
    return response;
  },

  update: async (id, leadData) => {
    const response = await api.put(`/leads/${id}`, leadData);
    return response;
  },

  delete: async (id) => {
    const response = await api.delete(`/leads/${id}`);
    return response;
  },

  getLookups: async () => {
    const response = await api.get('/leads/lookups');
    return response;
  },

  exportToCSV: async () => {
    const response = await api.get('/leads/export/csv', {
      responseType: 'blob',
    });
    return response.data;
  },

  getFollowUpHistory: async (leadId) => {
    const response = await api.get(`/leads/${leadId}/followups`);
    return response;
  },

  updateStatus: async (leadId, data) => {
    const response = await api.put(`/leads/${leadId}/status`, data);
    return response;
  },getBlueSkyLeads: async (filters = {}) => {
    const response = await api.get('/leads/blue-sky', { params: filters });
    return response;
  },

  getProposalPhaseLeads: async (filters = {}) => {
    const response = await api.get('/leads/proposal-phase', { params: filters });
    return response;
  },

  getNegotiationLeads: async (filters = {}) => {
    const response = await api.get('/leads/negotiation', { params: filters });
    return response;
  },
};
// ============================================
// PROJECT SERVICE
// ============================================

const projectService = {
  getActiveProjects: async (filters = {}) => {
    const response = await api.get('/projects', { params: filters });
    return response;
  },

  getArchivedProjects: async (filters = {}) => {
    const response = await api.get('/projects/archived', { params: filters });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/projects/${id}`);
    return response;
  },

  archive: async (id, feedbackData) => {
    const response = await api.post(`/projects/${id}/archive`, feedbackData);
    return response;
  },

  updateFeedback: async (id, feedbackData) => {
    const response = await api.put(`/projects/${id}/feedback`, feedbackData);
    return response;
  },
};

// ============================================
// NOTIFICATION SERVICE
// ============================================

const notificationService = {
  getStaleLeads: async () => {
    const response = await api.get('/notifications/stale-leads');
    return response;
  },

  getCount: async () => {
    const response = await api.get('/notifications/count');
    return response;
  },

  getAll: async (filters = {}) => {
    const response = await api.get('/notifications', { params: filters });
    return response;
  },

  acknowledge: async (leadId) => {
    const response = await api.post(`/notifications/${leadId}/acknowledge`);
    return response;
  },

  dismissAll: async () => {
    const response = await api.post('/notifications/dismiss-all');
    return response;
  },
};

// ============================================
// USER SERVICE
// ============================================

const userService = {
  getAll: async () => {
    const response = await api.get('/users');
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response;
  },

  create: async (userData) => {
    const response = await api.post('/users', userData);
    return response;
  },

  update: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response;
  },

  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response;
  },

  resetPassword: async (id, newPassword) => {
    const response = await api.post(`/users/${id}/reset-password`, {
      newPassword,
    });
    return response;
  },
};

// ============================================
// LOOKUP SERVICE
// ============================================

const lookupService = {
  getAll: async () => {
    const response = await api.get('/lookups/all');
    return response;
  },

  getConsultants: async () => {
    const response = await api.get('/lookups/consultants');
    return response;
  },

  getSectors: async () => {
    const response = await api.get('/lookups/sectors');
    return response;
  },

  getCountries: async () => {
    const response = await api.get('/lookups/countries');
    return response;
  },

  getStatuses: async () => {
    const response = await api.get('/lookups/statuses');
    return response;
  },

  getSources: async () => {
    const response = await api.get('/lookups/sources');
    return response;
  },

  getServices: async () => {
    const response = await api.get('/lookups/services');
    return response;
  },
};

// ============================================
// BACKUP SERVICE
// ============================================

const backupService = {
  createBackup: () => api.get('/backup/create', { 
    responseType: 'blob' 
  }),

  restoreBackup: (formData) => api.post('/backup/restore', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
};

// ============================================
// SINGLE EXPORT - NO DUPLICATES
// ============================================

export {
  api,
  authService,
  dashboardService,
  leadService,
  projectService,
  notificationService,
  userService,
  lookupService,
  backupService
};

export default api;